import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDownIcon, ArrowUpIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { listSlideLibraryItems } from '../../data/slideLibrary';
import { parseSlideIds, serializeSlideIds } from '../../data/slideCollectionValue';
import { describeApiError } from '../../data/http';
import type { SlideLibraryItem } from '../../data/types';
import type { TFunction } from 'i18next';

/** Matches a search term against both the slide's name and its AI-generated description. */
function matchesSearch(item: SlideLibraryItem, needle: string): boolean {
  return item.name.toLowerCase().includes(needle) || item.description.toLowerCase().includes(needle);
}

/** Deck name / uploader / AI description shown on hover, same shape as the Studio browse list. */
function slideTooltip(item: SlideLibraryItem, t: TFunction): string {
  return [
    item.deckName ? t('wizard.slides.deckLabel', { deckName: item.deckName }) : null,
    item.uploadedByName || item.uploadedByEmail
      ? t('wizard.slides.uploadedByLabel', { name: item.uploadedByName || item.uploadedByEmail })
      : null,
    item.description || t('wizard.slides.descriptionPending')
  ]
    .filter(Boolean)
    .join('\n');
}

interface SlideCollectionPickerProps {
  value: string;
  onChange: (value: string) => void;
  configJson: string;
}

function maxSlidesOf(configJson: string): number | null {
  try {
    const parsed = JSON.parse(configJson || '{}') as { maxSlides?: unknown };
    return typeof parsed.maxSlides === 'number' && parsed.maxSlides > 0 ? parsed.maxSlides : null;
  } catch {
    return null;
  }
}

const thumbClass = 'aspect-video w-full rounded border border-border bg-muted object-cover';
const iconButton =
  'inline-flex size-7 items-center justify-center rounded border border-input bg-surface text-muted-foreground transition hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40';

/** Wizard-time picker for a SlideCollection property: an ordered, multi-select from the Slide Library. */
export function SlideCollectionPicker({ value, onChange, configJson }: SlideCollectionPickerProps) {
  const { t } = useTranslation();

  const [items, setItems] = useState<SlideLibraryItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;

    listSlideLibraryItems()
      .then((loaded) => {
        if (!cancelled) setItems(loaded);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(describeApiError(err, t('wizard.slides.unavailable')));
      });

    return () => {
      cancelled = true;
    };
  }, [t]);

  const selectedIds = parseSlideIds(value);
  const byId = new Map((items ?? []).map((item) => [item.id, item]));
  const max = maxSlidesOf(configJson);
  const atMax = max !== null && selectedIds.length >= max;

  const setIds = (ids: string[]) => onChange(serializeSlideIds(ids));
  const add = (id: string) => {
    if (atMax || selectedIds.includes(id)) return;
    setIds([...selectedIds, id]);
  };
  const remove = (id: string) => setIds(selectedIds.filter((existing) => existing !== id));
  const move = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= selectedIds.length) return;
    const next = [...selectedIds];
    [next[index], next[target]] = [next[target], next[index]];
    setIds(next);
  };

  if (error) {
    return (
      <p role="alert" className="mt-2 border-l-4 border-destructive bg-destructive-soft px-4 py-3 text-sm text-destructive">
        {error}
      </p>
    );
  }

  if (items === null) {
    return (
      <p className="mt-2 text-sm text-muted-foreground" aria-live="polite">
        {t('wizard.slides.loading')}
      </p>
    );
  }

  const needle = search.trim().toLowerCase();
  const available = items
    .filter((item) => !selectedIds.includes(item.id))
    .filter((item) => !needle || matchesSearch(item, needle));

  return (
    <div className="mt-2 space-y-6">
      <div>
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t('wizard.slides.selectedTitle')}
        </h3>

        {selectedIds.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">{t('wizard.slides.selectedEmpty')}</p>
        ) : (
          <ul className="mt-2 grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {selectedIds.map((id, index) => {
              const item = byId.get(id);
              return (
                <li key={id} className="rounded-lg border border-border bg-surface p-2">
                  {item?.thumbnailUrl ? (
                    <img src={item.thumbnailUrl} alt="" title={slideTooltip(item, t)} className={thumbClass} />
                  ) : (
                    <div className={thumbClass} />
                  )}
                  <p className="mt-1 truncate text-xs text-foreground" title={item?.name ?? id}>
                    {item?.name ?? id}
                  </p>
                  <div className="mt-1 flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => move(index, -1)}
                      disabled={index === 0}
                      aria-label={t('wizard.slides.moveUp')}
                      className={iconButton}
                    >
                      <ArrowUpIcon className="size-3.5" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(index, 1)}
                      disabled={index === selectedIds.length - 1}
                      aria-label={t('wizard.slides.moveDown')}
                      className={iconButton}
                    >
                      <ArrowDownIcon className="size-3.5" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(id)}
                      aria-label={t('wizard.slides.remove')}
                      className={iconButton}
                    >
                      <XMarkIcon className="size-3.5" aria-hidden="true" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {atMax ? <p className="mt-2 text-xs text-muted-foreground">{t('wizard.slides.maxReached', { max })}</p> : null}
      </div>

      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('wizard.slides.empty')}</p>
      ) : (
        <div>
          <label htmlFor="slide-collection-search" className="sr-only">
            {t('wizard.slides.search')}
          </label>
          <input
            id="slide-collection-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('wizard.slides.searchPlaceholder')}
            className="mb-3 w-full rounded-sm border border-input bg-surface px-3 py-2 text-sm outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/22"
          />

          {available.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('wizard.slides.noMatches')}</p>
          ) : (
            <ul className="grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
              {available.map((item) => (
                <li key={item.id} className="rounded-lg border border-border bg-surface p-2">
                  {item.thumbnailUrl ? (
                    <img src={item.thumbnailUrl} alt="" title={slideTooltip(item, t)} className={thumbClass} />
                  ) : (
                    <div className={thumbClass} />
                  )}
                  <p className="mt-1 truncate text-xs text-foreground" title={item.name}>
                    {item.name}
                  </p>
                  <button
                    type="button"
                    onClick={() => add(item.id)}
                    disabled={atMax}
                    className="mt-1 inline-flex items-center gap-1 rounded border border-input bg-surface px-2 py-1 text-xs font-medium text-foreground transition hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <PlusIcon className="size-3.5" aria-hidden="true" />
                    {t('wizard.slides.add')}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
